import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    CircularProgress,
    Alert,
    SelectChangeEvent,
    Backdrop,
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Remove as RemoveIcon,
    Add as PlusIcon,
    Refresh as RefreshIcon,
} from '@mui/icons-material';
import FlashBetEventDto, { UpdateMatchEventsRequest } from '../models/dtos/FlashBetEventDto';
import {
    DEFAULT_FLASH_BET_EVENT_TYPE,
    FLASH_BET_EVENT_TYPES,
    FlashBetEventType,
} from '../constants/flashBetEventTypes';
import { FlashBetMatchDto } from '../models/dtos/FlashBetMatchDto';
import { getMatchEvents, updateMatchEvents, refetchMatchEvents } from '../Api';

interface EditMatchEventsModalProps {
    open: boolean;
    matchId: string;
    match?: FlashBetMatchDto | null;
    onClose: () => void;
    onSave?: () => void;
    onRefetch?: () => void;
}

const SUPPORTED_LANGUAGES = [
    { code: 'en', name: 'English' },
    { code: 'de', name: 'German' },
    { code: 'fr', name: 'French' },
    { code: 'it', name: 'Italian' },
    { code: 'ro', name: 'Romanian' },
    { code: 'ru', name: 'Russian' },
    { code: 'es', name: 'Spanish' },
    { code: 'pt', name: 'Portuguese' },
    { code: 'pl', name: 'Polish' },
    { code: 'tr', name: 'Turkish' },
];

interface EventState extends FlashBetEventDto {
    isNew?: boolean;
}

const EditMatchEventsModal: React.FC<EditMatchEventsModalProps> = ({
    open,
    matchId,
    match,
    onClose,
    onSave,
    onRefetch,
}) => {
    const [events, setEvents] = useState<EventState[]>([]);
    const [originalEvents, setOriginalEvents] = useState<FlashBetEventDto[]>([]);
    const [selectedLanguage, setSelectedLanguage] = useState('en');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [refetching, setRefetching] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (open && matchId) {
            loadEvents();
        }
    }, [open, matchId]);

    const loadEvents = async () => {
        try {
            setLoading(true);
            setError(null);
            const eventsData = await getMatchEvents(matchId);
            if (eventsData) {
                const normalizedEvents = eventsData.map((event) => ({
                    ...event,
                    eventType: event.eventType ?? DEFAULT_FLASH_BET_EVENT_TYPE,
                }));
                setEvents(normalizedEvents);
                setOriginalEvents(normalizedEvents);
            } else {
                setEvents([]);
                setOriginalEvents([]);
            }
        } catch (error) {
            console.error('Failed to load events:', error);
            setError('Failed to load events');
        } finally {
            setLoading(false);
        }
    };

    const handleLanguageChange = (event: SelectChangeEvent) => {
        setSelectedLanguage(event.target.value);
    };

    const handleEventTextChange = (eventIndex: number, text: string) => {
        setEvents(prevEvents => {
            const newEvents = [...prevEvents];
            if (selectedLanguage === 'en') {
                newEvents[eventIndex].text = text;
            } else {
                if (!newEvents[eventIndex].localizations) {
                    newEvents[eventIndex].localizations = {};
                }
                newEvents[eventIndex].localizations[selectedLanguage] = text;
            }
            return newEvents;
        });
    };

    const handleMinuteChange = (eventIndex: number, increment: boolean) => {
        setEvents(prevEvents => {
            const newEvents = [...prevEvents];
            const currentMinute = newEvents[eventIndex].minute;
            let newMinute = increment ? currentMinute + 1 : currentMinute - 1;
            
            // Ensure minute is between 1 and 1000
            newMinute = Math.max(1, Math.min(1000, newMinute));
            newEvents[eventIndex].minute = newMinute;
            
            // Sort events by minute to maintain ascending order
            newEvents.sort((a, b) => a.minute - b.minute);
            
            return newEvents;
        });
    };

    const handleTeamTypeChange = (eventIndex: number, teamType: 'HOME' | 'AWAY') => {
        setEvents(prevEvents => {
            const newEvents = [...prevEvents];
            newEvents[eventIndex].teamType = teamType;
            return newEvents;
        });
    };

    const handleEventTypeChange = (eventIndex: number, eventType: FlashBetEventType) => {
        setEvents(prevEvents => {
            const newEvents = [...prevEvents];
            newEvents[eventIndex].eventType = eventType;
            return newEvents;
        });
    };

    const handleDeleteEvent = (eventIndex: number) => {
        setEvents(prevEvents => prevEvents.filter((_, index) => index !== eventIndex));
    };

    const handleAddEvent = (afterIndex: number) => {
        const baseEvent = events[afterIndex];
        const newEvent: EventState = {
            id: `temp-${Date.now()}`, // Temporary ID for new events
            minute: baseEvent.minute,
            text: '',
            localizations: {},
            teamType: baseEvent.teamType,
            eventType: DEFAULT_FLASH_BET_EVENT_TYPE,
            isNew: true,
        };
        
        setEvents(prevEvents => {
            const newEvents = [...prevEvents];
            newEvents.splice(afterIndex + 1, 0, newEvent);
            return newEvents;
        });
    };

    const getDisplayText = (event: EventState) => {
        if (selectedLanguage === 'en') {
            return event.text;
        }
        
        const translatedText = event.localizations?.[selectedLanguage];
        return translatedText || '';
    };

    const getPlaceholderText = (event: EventState) => {
        if (selectedLanguage === 'en') {
            return 'Enter event text (required)';
        }
        return event.text || 'English translation will appear here';
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            setError(null);

            // Check for events with empty English text
            const eventsWithEmptyText = events.filter(event => event.text.trim() === '');
            if (eventsWithEmptyText.length > 0) {
                setError('All events must have English text. Please fill in the missing text fields or delete the empty events.');
                return;
            }

            // Filter out events with empty English text (this should now be empty array due to validation above)
            const validEvents = events.filter(event => event.text.trim() !== '');
            
            // Allow saving even if there are no events (empty list)

            // Create update request
            const updateRequest: UpdateMatchEventsRequest = {
                events: validEvents.map(event => ({
                    id: event.isNew ? '' : event.id, // Empty ID for new events
                    minute: event.minute,
                    text: event.text,
                    localizations: event.localizations || {},
                    teamType: event.teamType,
                    eventType: event.eventType ?? DEFAULT_FLASH_BET_EVENT_TYPE,
                })),
            };

            const updatedEvents = await updateMatchEvents(matchId, updateRequest);
            if (updatedEvents) {
                onSave?.();
                onClose();
            } else {
                setError('Failed to save events');
            }
        } catch (error) {
            console.error('Failed to save events:', error);
            setError('Failed to save events');
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        setEvents(originalEvents);
        setSelectedLanguage('en');
        setError(null);
        onClose();
    };

    const handleRefetch = async () => {
        try {
            setRefetching(true);
            setError(null);
            
            const updatedMatch = await refetchMatchEvents(matchId);
            if (updatedMatch) {
                // Reload the events after refetch
                await loadEvents();
                // Call the parent callback to reload the match list
                onRefetch?.();
            } else {
                setError('Failed to refetch events');
            }
        } catch (error) {
            console.error('Failed to refetch events:', error);
            setError('Failed to refetch events');
        } finally {
            setRefetching(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleCancel} maxWidth="lg" fullWidth>
            <DialogTitle>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                        <Typography variant="h6">Edit Match Events</Typography>
                        {match && (
                            <>
                                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 0.5 }}>
                                    {match.homeTeamDto?.name} vs {match.awayTeamDto?.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', mt: 0.25, display: 'block' }}>
                                    Total: {events.length} events
                                </Typography>
                            </>
                        )}
                    </Box>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Language</InputLabel>
                        <Select
                            value={selectedLanguage}
                            onChange={handleLanguageChange}
                            label="Language"
                        >
                            {SUPPORTED_LANGUAGES.map((lang) => (
                                <MenuItem key={lang.code} value={lang.code}>
                                    {lang.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </DialogTitle>

            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {loading ? (
                    <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <TableContainer component={Paper} sx={{ mt: 1 }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Minute</TableCell>
                                    <TableCell>Team</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Event Text ({selectedLanguage === 'en' ? 'English' : SUPPORTED_LANGUAGES.find(l => l.code === selectedLanguage)?.name})</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {events.map((event, index) => (
                                    <TableRow 
                                        key={`${event.id}-${index}`} 
                                        sx={{ 
                                            backgroundColor: event.isNew ? 'rgba(25, 118, 210, 0.08)' : 'inherit',
                                        }}
                                    >
                                        <TableCell>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleMinuteChange(index, false)}
                                                    disabled={event.minute <= 1}
                                                >
                                                    <RemoveIcon fontSize="small" />
                                                </IconButton>
                                                <Typography variant="body2" sx={{ minWidth: 30, textAlign: 'center' }}>
                                                    {event.minute}
                                                </Typography>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleMinuteChange(index, true)}
                                                    disabled={event.minute >= 1000}
                                                >
                                                    <PlusIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <FormControl size="small" sx={{ minWidth: 80 }}>
                                                <Select
                                                    value={event.teamType}
                                                    onChange={(e) => handleTeamTypeChange(index, e.target.value as 'HOME' | 'AWAY')}
                                                    variant="outlined"
                                                >
                                                    <MenuItem value="HOME">HOME</MenuItem>
                                                    <MenuItem value="AWAY">AWAY</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </TableCell>
                                        <TableCell>
                                            <FormControl size="small" sx={{ minWidth: 140 }}>
                                                <Select
                                                    value={event.eventType ?? DEFAULT_FLASH_BET_EVENT_TYPE}
                                                    onChange={(e) => handleEventTypeChange(index, e.target.value as FlashBetEventType)}
                                                    variant="outlined"
                                                >
                                                    {FLASH_BET_EVENT_TYPES.map((type) => (
                                                        <MenuItem key={type.value} value={type.value}>
                                                            {type.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </TableCell>
                                        <TableCell>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={2}
                                                value={getDisplayText(event)}
                                                onChange={(e) => handleEventTextChange(index, e.target.value)}
                                                placeholder={getPlaceholderText(event)}
                                                variant="outlined"
                                                size="small"
                                                sx={{
                                                    '& .MuiInputBase-input::placeholder': {
                                                        color: selectedLanguage !== 'en' && event.text ? 'rgba(0, 0, 0, 0.4)' : 'rgba(0, 0, 0, 0.6)',
                                                    }
                                                }}
                                            />
                                            {/* Translation badges */}
                                            <Box display="flex" gap={0.5} mt={1} flexWrap="wrap">
                                                {event.text && (
                                                    <Chip
                                                        label="en"
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: '#1976d2',
                                                            color: 'white',
                                                            fontSize: '0.7rem',
                                                            height: '20px',
                                                        }}
                                                    />
                                                )}
                                                {event.localizations && Object.keys(event.localizations).map((langCode) => {
                                                    // Only show languages that have actual translations
                                                    if (event.localizations[langCode] && event.localizations[langCode].trim() !== '') {
                                                        return (
                                                            <Chip
                                                                key={langCode}
                                                                label={langCode}
                                                                size="small"
                                                                sx={{
                                                                    backgroundColor: '#1976d2',
                                                                    color: 'white',
                                                                    fontSize: '0.7rem',
                                                                    height: '20px',
                                                                }}
                                                            />
                                                        );
                                                    }
                                                    return null;
                                                })}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box display="flex" gap={0.5}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleDeleteEvent(index)}
                                                    color="error"
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleAddEvent(index)}
                                                    color="primary"
                                                >
                                                    <AddIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        {events.length === 0 && (
                            <Box p={3} textAlign="center">
                                <Typography color="textSecondary">
                                    No events found for this match.
                                </Typography>
                            </Box>
                        )}
                    </TableContainer>
                )}
            </DialogContent>

            <DialogActions>
                <Button 
                    onClick={handleRefetch} 
                    disabled={saving || refetching || loading}
                    startIcon={refetching ? <CircularProgress size={20} /> : <RefreshIcon />}
                    sx={{ mr: 'auto' }}
                >
                    {refetching ? 'Re-fetching...' : 'Re-fetch'}
                </Button>
                <Button onClick={handleCancel} disabled={saving || refetching}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={saving || refetching}
                    startIcon={saving ? <CircularProgress size={20} /> : null}
                >
                    {saving ? 'Saving...' : 'Save Events'}
                </Button>
            </DialogActions>

            {/* Loading Overlay */}
            <Backdrop
                sx={{ 
                    color: '#fff', 
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    position: 'absolute',
                    backgroundColor: 'rgba(0, 0, 0, 0.7)'
                }}
                open={refetching}
            >
                <Box display="flex" flexDirection="column" alignItems="center">
                    <CircularProgress color="inherit" />
                    <Typography variant="body2" sx={{ mt: 2 }}>
                        Re-fetching events from BetsAPI...
                    </Typography>
                </Box>
            </Backdrop>
        </Dialog>
    );
};

export default EditMatchEventsModal;